// styling import

import Slider from "@mui/material/Slider";
import { Typography } from "@mui/material";
import { withStyles } from "@mui/styles";

import './AppSlider.scss';
import { formatNumToTime, formatTimeToNum } from "../../helpers/timeHelpers";
import React from "react";
import { useState } from "react";

// define the style for the slider
const StyledAppSlider = withStyles({
    root: {
        color: '#005078'
    },
    track: {
        color: "#005078",
        height: '6px',
        marginTop: '-1px'
    },
    thumb: {
        width: '18px',
        height: '18px',
        marginTop: '-8px',
        marginLeft: '-9px'
    },
    active: {},
    valueLabel: {
        left: 'calc(-50% - 0px)',
        top: 35,
        border: '1px solid #d8d8d8',
        margin: 'auto',
        alignItems: "center",
        height: '3em',
        '& *': {
            background: 'transparent',
            color: '#97999c',
            fontSize: "1.4em",
            fontWeight: "600",
            top: '50%',
            transform: 'translateY(-10%)'
        }
    }
})(Slider);

/**
 * This component is the slider used for filtering 
 * @param param0 Props that are passed into the component
 *      label { String } | This is the label for the Slider - will be displayed over top of the slider
 *      min { Number } | This is the minimum value that can be accessed using the slider
 *      max { Number } | This is the maximum value that can be accessed using the slider
 *      value { Number[] - [currentMin, currentMax] } | This is the current min and max values that the user has chosen
 *      onChange { Function () } | This is the onChange function that updates the values that are passed into the component from a higher component
 *      name { String } | This is the name used to identify the slider - this allows the onChange function identify whichn attribute to change
 * @returns The AppSlider Component
 */

 function AppSlider({label="", desc="", min, max, value, onChange, name, type = "time"}) {

    const [isFocused, setIsFocused] = useState(false);
    const [range, setRange] = useState({
        min: value[0] === -1 ? min : value[0],
        max: value[1] === -1 ? max: value[1]
    });

    // TODO: Need to create a format function to be used by the slider - could be a Prop?
    // Event handler for slider change
    const changeSlider = (event, value) => {
        const minRange = type === 'time' ? formatNumToTime(value[0]) : formatNumToPercentage(value[0]);
        const maxRange = type === 'time' ? formatNumToTime(value[1]) : formatNumToPercentage(value[1]);
        setRange({min: minRange, max: maxRange});
        onChange({target: {name: name}}, value);
    }

    let keydown = 0;
    const handleKeyDown = (event) => {
        keydown = event.keyCode; 
    }
      
    const handleTextChange = (event, rangeField) => {
        if(type === 'time') handleTimeTextChange(event, rangeField);
        else handlePercentageTextChange(event, rangeField);
        setIsFocused(true);
    }

    const handlePercentageTextChange = (event, rangeField) => {
        let val = event.target.value;
        val = val.replace(/[^\d]/, ''); 
        val = val.substring(0, Math.min(val.length, 2)); 

        const inputValue = rangeField === 'min' ? [val, formatNumToPercentage(value[1])] : [formatNumToPercentage(value[0]), val]
        const sliderValue = rangeField === 'min' ? [Number(val),value[1]] : [value[0],Number(val)];
        setRange({ min: inputValue[0], max: inputValue[1]});
        onChange({target: {name: name}}, sliderValue);
    }

    const handleTimeTextChange = (event, rangeField) => {
        let val = event.target.value;
        if(keydown === 186 && [...val].filter(v=> v === ':').length > 1) return;
        if(keydown === 186 && (val.length !== 2 && val.length !== 3 && val.length !== 4)) return; 
        val = val.replace(/[^\d:]/, '');           

        const hoursMoreThanTwoDigits = formatNumToTime(max).length > 5 ? true : false;
        if(hoursMoreThanTwoDigits) val.includes(':') ? val = val.substring(0, Math.min(val.length, 6)) : val = val.substring(0, Math.min(val.length, 5)); 
        else val.includes(':') ? val = val.substring(0, Math.min(val.length, 5)) : val = val.substring(0, Math.min(val.length, 4)); 

        if(val.includes(':')) {
            //Prevent hhh from being more than 3 digits or hh or mm from being more than 2 digits
            const hours = val.split(':')[0];
            const minutes = val.split(':')[1];
            if(hoursMoreThanTwoDigits) {
                if(hours.length > 3 || minutes.length > 2) return;
            } else {
                if(hours.length > 2 || minutes.length > 2) return;
            }
        } 
        const inputValue = rangeField === 'min' ? [val, formatNumToTime(value[1])] : [formatNumToTime(value[0]), val];
        const sliderValue = rangeField === 'min' ? [formatTimeToNum(val), value[1]] : [value[0], formatTimeToNum(val)];
        setRange({ min: inputValue[0], max: inputValue[1]});
        onChange({target: {name: name}}, sliderValue);
    }

    const handleBlur = (event, rangeField) => {
        if(type === 'time') handleTimeBlur(event, rangeField);
        else handlePercentageBlur(event, rangeField);
        setIsFocused(false);
    }

    /**
     * Resetting percentage input to default values for incorrect values
     * Format correct value to include % at the end
     * @param event 
     * @param rangeField 
     */
    const handlePercentageBlur = ((event, rangeField) => {
        let val = event.target.value; 
        if(val === '') {
            resetInputField(rangeField);
            resetSlider(rangeField);
        } else {
            //Prevent min/max crossover (allow them to be equal) and prevent min range from going lower than min amount or max range going above max
            if((rangeField === 'min' && (val > value[1] || val < min)) || (rangeField === 'max' && (val < value[0] || val > max))) { 
                resetInputField(rangeField); 
                resetSlider(rangeField); 
            } else if(!val.includes('%')) setRange({...range, [rangeField]: formatNumToPercentage(val)})
        }
    })

    /**
     * Format correct value for leading and trailing 0s
     * @param event 
     * @param rangeField 
     */
    const handleTimeBlur = ((event, rangeField) => {
        let val = event.target.value;
        //Prevent min/max crossover (allow them to be equal) and prevent min range from going lower than min amount or max range going above max
        if((rangeField === 'min' && (formatTimeToNum(val) > value[1] || formatTimeToNum(val) < min)) || (rangeField === 'max' && (formatTimeToNum(val) < value[0] || formatTimeToNum(val) > max))) { 
            resetInputField(rangeField); 
            resetSlider(rangeField); 
        } else setRange({...range, [rangeField]: formatNumToTime(formatTimeToNum(val))}); 
    })

    /**
     * Reset the slider to default min/max values
     * @param rangeField 
     */
    function resetSlider(rangeField) {
        const sliderValues = rangeField === 'min' ? [min,value[1]] : [value[0],max];
        onChange({target: {name: name}}, sliderValues)
    }

    /**
     * Reset the input field to default min/max values
     * @param rangeField 
     */
    function resetInputField(rangeField) {
        const rangeBound = rangeField === 'min' ? min : max;
        if(type === 'time') setRange({...range, [rangeField]: formatNumToTime(rangeBound)})
        else setRange({...range, [rangeField]: formatNumToPercentage(rangeBound)})
    }

    function formatNumToPercentage(num) {
        return num+'%';
    }

    /**
     * SetInput min and max range values when slider is rendered (even while user is typing because component is rerendered whenever there is a text change) 
     * If value is [-1,-1] = default, then display min max props as initial input text range, else display values
     * @param rangeType 
     * @returns 
     */
     function setInputValue(rangeType) {
        let val = rangeType === 'min' ? value[0]=== -1 ? min : value[0]: value[1]=== -1 ? max : value[1];
        let formattedVal = type === 'time' ? formatNumToTime(val) : formatNumToPercentage(val);
        if(isFocused) return range[rangeType];
        return formattedVal;
      }

    function setMarker(markerValue) {
        switch (type) {
            case "percentage":
                return markerValue + "%";
            case "time":
                return formatNumToTime(markerValue);
            default:
                return markerValue;
        }
    }

    const markers = [
        {
            value: min === 0 ? '' : min,
            label: setMarker(min)
        },
        {
            value: max,
            label: setMarker(max)
        }
    ]
    return (
        <div className='app-slider'>
            {/* Label */}
            <Typography id="range-slider-hrs" gutterBottom >
                <span className='slider-label'>{label}</span>
            </Typography>
            {/* description */}
            <Typography id="range-slider-hrs" gutterBottom >
                <span className='slider-desc'>{desc}</span>
            </Typography>
            <div className="slider-container">
                <div className="current-min">
                    <input
                        value={setInputValue('min')} 
                        onChange={(e) => handleTextChange(e, 'min')} 
                        onKeyDown={(e) => handleKeyDown(e)} 
                        onBlur={(e) => handleBlur(e, 'min')}
                    />
                </div>
                <div className="slider-component">
                    {/* Slider component */}
                    <StyledAppSlider
                        name={name}
                        min={min}
                        max={max}
                        marks = {markers}
                        value={[value[0] === -1 ? min : value[0], value[1] === -1 ? max : value[1]]}
                        onChange={changeSlider}
                        aria-labelledby='range-slider-hrs'
                    />
                </div>
                <div className="current-max">
                    <input
                        value={setInputValue('max')} 
                        onChange={(e) => handleTextChange(e, 'max')} 
                        onKeyDown={(e) => handleKeyDown(e)} 
                        onBlur={(e) => handleBlur(e, 'max')}
                    />
                </div>
            </div>
        </div>
    )
}

export default React.memo(AppSlider);
